import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Compass } from "lucide-react";
import { usePageSeo } from "@/hooks/usePageSeo";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import heroImg from "@/assets/hero-destinations.jpg";
import { cn } from "@/lib/utils";

interface Destination {
  id: string; slug: string; title: string;
  short_description: string | null; main_image_url: string | null;
}

const DestinationsPage = () => {
  usePageSeo("destinations");
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from("destinations")
      .select("id,slug,title,short_description,main_image_url,sort_order")
      .eq("is_active", true)
      .order("sort_order")
      .then(({ data }) => {
        setDestinations((data as Destination[]) || []);
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <PageHero
        eyebrow="Destinations"
        title={<>The Dominican Republic, <span className="italic text-gradient-ocean">one paradise at a time.</span></>}
        subtitle="From turquoise sandbars to colonial cobblestones, every corner of the island has a story. Pick the next chapter of yours."
        image={heroImg}
      />

      <section className="container mx-auto px-4 py-20">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] rounded-3xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : destinations.length === 0 ? (
          <div className="text-center py-20 max-w-md mx-auto">
            <Compass className="w-12 h-12 mx-auto text-primary/60" />
            <h3 className="font-display text-2xl font-semibold mt-5">Destinations coming soon</h3>
            <p className="text-muted-foreground mt-2">
              We're putting the final touches on our destination guides. Check back shortly.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {destinations.map((d, i) => (
              <Link
                key={d.id}
                to={`/destinations/${d.slug}`}
                className={cn(
                  "group relative overflow-hidden rounded-3xl bg-card shadow-card hover:shadow-elegant transition-smooth",
                  i % 5 === 0 && "lg:col-span-2 lg:row-span-1",
                )}
              >
                <div className={cn("overflow-hidden bg-muted", i % 5 === 0 ? "aspect-[16/10]" : "aspect-[4/5]")}>
                  {d.main_image_url ? (
                    <img src={d.main_image_url} alt={d.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-smooth duration-700" />
                  ) : (
                    <div className="w-full h-full bg-gradient-ocean" />
                  )}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--hero-bg))]/95 via-[hsl(var(--hero-bg))]/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                  <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-primary-glow">
                    <MapPin className="w-3.5 h-3.5" /> Dominican Republic
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-semibold mt-1.5">{d.title}</h3>
                  {d.short_description && (
                    <p className="text-sm text-white/80 mt-1.5 line-clamp-2 max-w-md">{d.short_description}</p>
                  )}
                  <div className="mt-4 inline-flex items-center gap-2 text-primary-glow font-medium text-sm">
                    Discover <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default DestinationsPage;
